import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import EmbeddedXrayViewer, {
  type EmbeddedXrayViewerHandle,
  type EmbeddedXraySelectedFile,
} from '@components/common/EmbeddedXrayViewer'
import ChartingSidePanel from './ChartingSidePanel.component'
import ChartingHistory from './ChartingHistory.component'

import ChartingViewToggle, {
  ChartingView,
} from './ChartingViewToggle.component'
import {
  ApplyConditionPayload,
  IDentalChart,
} from '@dataTypes/chartingFeature.types'
import { filterDentalDataByLayers } from '@utils/dental-data.utils'
import { applyConditionPayloadsToDentalData } from '@utils/dental-condition-application.utils'
import { mergeDentalChartWithCompletedProcedures } from '@utils/treatment-plan.utils'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import { buildPerioSitesByTooth } from '@utils/perio-line.utils'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  GridComponent,
  RecordClickEventArgs,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids'
import {
  setDentalUpdated,
  setSelectedDentalChart,
} from '@store/reducers/charting.reducer'
import { EmptyPerioChart } from '@constants/Charting'
import ToothChart from './ToothChart.component'
import { useActiveDentalChart } from '@hooks/useActiveDentalChart'
import {
  CHART_XRAY_HANDLE_PX,
  CHART_XRAY_PANE_MAX_PCT,
  CHART_XRAY_PANE_MIN_PCT,
  useChartXraySplit,
} from './useChartXraySplit.hook'

interface ChartingMainProps {
  onViewNote: (id: string | null) => void
  layers: Record<string, boolean>
  isFullscreen: boolean
  isXrayPanelOpen: boolean
  gridRef: React.RefObject<GridComponent>
  validationErrors: { [key: string]: string }
  setValidationErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string
    }>
  >
  initialEmbeddedFiles: EmbeddedXraySelectedFile[] | null
  /** Page-level charting fetch + SVG preload finished for the active patient. */
  chartBootstrapComplete?: boolean
  /** False while this tab is kept mounted but hidden (avoids a 0×0 OHIF viewport). */
  isHostVisible?: boolean
  voiceStatus?: React.ReactNode
  /** When true (mic connecting/listening/processing), open Voice assistant. */
  voiceActive?: boolean
  /** Bumped when manual save fails on Charting information fields. */
  chartingInfoFocusTick?: number
  voiceTooth?: number
  onSelectedToothChange?: (tooth: number | null) => void
}

const ChartingMain = ({
  onViewNote,
  layers,
  isFullscreen,
  isXrayPanelOpen,
  gridRef,
  validationErrors,
  setValidationErrors,
  initialEmbeddedFiles,
  chartBootstrapComplete = false,
  isHostVisible = true,
  voiceStatus,
  voiceActive = false,
  chartingInfoFocusTick = 0,
  voiceTooth,
  onSelectedToothChange,
}: ChartingMainProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const compareCharts = useAppSelector((state) => state.charting.compareCharts)
  const dentalCharts = useAppSelector((state) => state.charting.dentalCharts)
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )

  const [selectedTooth, setSelectedTooth] = useState<number | null>(null)
  const embeddedViewerRef = useRef<EmbeddedXrayViewerHandle | null>(null)
  const skippingVoiceToothReport = useRef(false)

  // Only push voice-driven tooth changes into the UI — never fight manual selection.
  useEffect(() => {
    if (typeof voiceTooth !== 'number') return
    skippingVoiceToothReport.current = true
    setSelectedTooth(voiceTooth)
  }, [voiceTooth])

  useEffect(() => {
    if (skippingVoiceToothReport.current) {
      skippingVoiceToothReport.current = false
      return
    }
    onSelectedToothChange?.(selectedTooth)
  }, [selectedTooth, onSelectedToothChange])

  // Tooth click → ask the embedded viewer to navigate to the linked study
  // (Phase 7.4). The mapping table (tooth → study/series) is the back-end's
  // responsibility; until it lands, we forward the existing dental chart
  // annotation that already carries a `study_uid` if present.
  useEffect(() => {
    if (selectedTooth == null) return
    try {
      const activeChart =
        selectedDentalChart &&
        selectedDentalChart.chart_id !== createDentalChart.chart_id
          ? selectedDentalChart
          : createDentalChart
      const entry = (activeChart?.dental_data ?? []).find(
        (d: any) => d?.tooth_id === selectedTooth,
      ) as any
      const studyUid: string | undefined = entry?.study_uid || entry?.studyUid
      const seriesUid: string | undefined =
        entry?.series_uid || entry?.seriesUid
      if (studyUid) {
        embeddedViewerRef.current?.navigateToStudy(studyUid, seriesUid)
      }
    } catch {
      // best-effort — tooth→study link is optional
    }
  }, [selectedTooth, selectedDentalChart, createDentalChart])

  const [currentView, setCurrentView] = useState<ChartingView>('full')
  const [isAnimating, setIsAnimating] = useState(false)
  const [animationDirection, setAnimationDirection] = useState<'up' | 'down'>(
    'down',
  )
  const [animatingRow, setAnimatingRow] = useState<'upper' | 'lower' | null>(
    null,
  )
  const [previousView, setPreviousView] = useState<ChartingView>('full')
  const isPerio = layers.perio
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const loading = useAppSelector((state) => state.charting.loading)
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans ?? [],
  )
  const { showCompletedProcedures } = useClinicChartingPreferences()
  const { activeDentalChart, isPersistedChart, commitActiveDentalChartUpdate } =
    useActiveDentalChart()
  const activeDentalChartId = useMemo((): string => {
    if (isPersistedChart && activeDentalChart.chart_id) {
      return activeDentalChart.chart_id
    }
    return createDentalChart.chart_id || 'new-chart'
  }, [activeDentalChart.chart_id, createDentalChart.chart_id, isPersistedChart])
  const dentalChartById = useMemo((): Record<string, IDentalChart> => {
    return dentalCharts.reduce<Record<string, IDentalChart>>(
      (accumulator, chart) => {
        accumulator[chart.chart_id] = chart
        return accumulator
      },
      {},
    )
  }, [dentalCharts])
  const toothChartRenderKey = selectedPatient?.patient_id ?? 'no-patient'

  const showXraySplit = isXrayPanelOpen && !isFullscreen

  const {
    splitRef: chartXraySplitRootRef,
    chartPaneFraction: chartXrayPaneFraction,
    onHandleMouseDown: onChartXraySplitHandleMouseDown,
  } = useChartXraySplit(showXraySplit)

  /** Only attach resize/drag measurements while split is visible; keeps RO off the normal layout path. */
  const attachChartXraySplitRef = useCallback(
    (node: HTMLDivElement | null): void => {
      if (showXraySplit && node !== null) {
        chartXraySplitRootRef(node)
      } else {
        chartXraySplitRootRef(null)
      }
    },
    [showXraySplit, chartXraySplitRootRef],
  )

  // Same active-chart rule as ChartingSidePanel (history row vs in-progress draft).
  const dentalData = useMemo(() => {
    if (!selectedPatient || loading) {
      return []
    }

    if (activeDentalChart.patient_id !== selectedPatient.patient_id) {
      return []
    }

    return activeDentalChart.dental_data ?? []
  }, [activeDentalChart, selectedPatient, loading])

  // Handle view change with animation
  const handleViewChange = (newView: ChartingView): void => {
    if (newView === currentView || isAnimating) return

    // Determine which row is being added/removed
    let rowToAnimate: 'upper' | 'lower' | null = null
    let direction: 'up' | 'down'

    if (previousView === 'full') {
      // full → upper: lower row removed, fade down
      // full → lower: upper row removed, fade up
      if (newView === 'upper') {
        rowToAnimate = 'lower'
        direction = 'down'
      } else {
        rowToAnimate = 'upper'
        direction = 'up'
      }
    } else if (previousView === 'upper') {
      // upper → full: lower row added, fade up
      // upper → lower: upper row removed and lower row added
      if (newView === 'full') {
        rowToAnimate = 'lower'
        direction = 'up'
      } else {
        // upper → lower: animate both, but we'll focus on the one being added
        rowToAnimate = 'lower'
        direction = 'up'
      }
    } else {
      // lower → full: upper row added, fade down
      // lower → upper: lower row removed and upper row added
      if (newView === 'full') {
        rowToAnimate = 'upper'
        direction = 'down'
      } else {
        // lower → upper: animate both, but we'll focus on the one being added
        rowToAnimate = 'upper'
        direction = 'down'
      }
    }

    setPreviousView(currentView)
    setAnimationDirection(direction)
    setAnimatingRow(rowToAnimate)
    setIsAnimating(true)
    setCurrentView(newView)

    // Reset animation state after animation completes
    setTimeout(() => {
      setIsAnimating(false)
      setAnimatingRow(null)
    }, 300) // Match CSS transition duration
  }

  const dentalDataForDisplay = useMemo(() => {
    return mergeDentalChartWithCompletedProcedures(
      dentalData,
      treatmentPlans,
      selectedPatient?.patient_id,
      showCompletedProcedures,
    )
  }, [
    dentalData,
    treatmentPlans,
    selectedPatient?.patient_id,
    showCompletedProcedures,
  ])

  // Define which conditions belong to which layer
  // Get filtered dental data based on layers
  const filteredDentalData = useMemo(() => {
    return filterDentalDataByLayers(dentalDataForDisplay, layers)
  }, [dentalDataForDisplay, layers])

  const handleSelectHistory = useCallback(
    (row: RowSelectEventArgs): void => {
      const args = row as RecordClickEventArgs
      // Let Syncfusion finish row selection DOM updates before chart re-render.
      window.queueMicrotask(() => {
        if (args.name === 'rowDeselected') {
          dispatch(setSelectedDentalChart(null))
          dispatch(setDentalUpdated(false))
          return
        }
        const rawChartData = row.data as
          | (IDentalChart & { column?: unknown })
          | undefined
        if (!rawChartData) {
          return
        }
        // Syncfusion may attach a transient `column` prop on row data.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { column, ...cleanChartData } = rawChartData

        const fullChartData = dentalChartById[cleanChartData.chart_id]

        if (!fullChartData) {
          // eslint-disable-next-line no-console
          console.warn(
            'Chart does not exist in dentalCharts',
            cleanChartData.chart_id,
          )
        }

        // Validate that the selected chart belongs to the current patient
        if (selectedPatient && fullChartData) {
          if (fullChartData.patient_id !== selectedPatient.patient_id) {
            dispatch(setSelectedDentalChart(null))
            dispatch(setDentalUpdated(false))
            return
          }
        }

        // Viewing history is not an edit session — clear dirty so Save/Reset stay off.
        dispatch(setDentalUpdated(false))
        if (fullChartData) {
          dispatch(setSelectedDentalChart(fullChartData))
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            'Full chart data not found, using cleanChartData',
            cleanChartData.chart_id,
          )
          // Validate patient_id even when using cleanChartData
          if (
            selectedPatient &&
            cleanChartData.patient_id !== selectedPatient.patient_id
          ) {
            dispatch(setSelectedDentalChart(null))
            return
          }
          dispatch(setSelectedDentalChart(cleanChartData as IDentalChart))
        }
      })
    },
    [dentalChartById, dispatch, selectedPatient],
  )

  const handleApplyCondition = (
    conditionOrConditions: ApplyConditionPayload | ApplyConditionPayload[],
  ): void => {
    if (selectedTooth === null) return

    // Must match ChartingSidePanel `activeChart` / dentalData resolution.
    const chartToModify = activeDentalChart

    dispatch(setDentalUpdated(true))
    const updatedDentalData = applyConditionPayloadsToDentalData({
      dentalData: chartToModify.dental_data ?? [],
      selectedTooth,
      conditionOrConditions,
    })

    commitActiveDentalChartUpdate({
      ...chartToModify,
      dental_data: updatedDentalData,
    })
  }

  const perioSitesByTooth = useMemo(() => {
    if (!isPerio) {
      return undefined
    }
    if (!selectedPatient || loading) {
      return buildPerioSitesByTooth(EmptyPerioChart.perio_data)
    }
    let chartToUse = createPerioChart
    if (compareCharts) {
      chartToUse = compareCharts.perioChartA
    } else if (
      selectedPerioChart &&
      selectedPerioChart.patient_id === selectedPatient.patient_id
    ) {
      chartToUse = selectedPerioChart
    } else if (createPerioChart.patient_id === selectedPatient.patient_id) {
      chartToUse = createPerioChart
    } else {
      chartToUse = EmptyPerioChart
    }
    return buildPerioSitesByTooth(
      chartToUse.perio_data,
      compareCharts?.perioChartB?.perio_data,
    )
  }, [
    isPerio,
    selectedPatient,
    loading,
    createPerioChart,
    compareCharts,
    selectedPerioChart,
  ])

  const toothChartElement = (
    <ToothChart
      key={toothChartRenderKey}
      dentalData={filteredDentalData}
      selectedTooth={selectedTooth}
      setSelectedTooth={setSelectedTooth}
      isPerio={isPerio}
      perioSitesByTooth={perioSitesByTooth}
      view={currentView}
      isAnimating={isAnimating}
      animationDirection={animationDirection}
      animatingRow={animatingRow}
      isDataLoading={loading}
      assetsPreloaded={chartBootstrapComplete}
      allowUpscale={showXraySplit}
    />
  )

  return (
    <>
      <Row className="gx-2 charting-main-row">
        <Col>
          <div
            ref={attachChartXraySplitRef}
            className={
              showXraySplit
                ? 'charting-chart-xray-split d-flex flex-row align-items-stretch w-100'
                : undefined
            }
          >
            <div
              className={
                showXraySplit
                  ? 'charting-chart-xray-split-chart-column charting-chart-xray-split-chart-column--half-vertical d-flex flex-column gap-2 min-h-0'
                  : 'charting-chart-layout-full w-100'
              }
              style={
                showXraySplit
                  ? {
                      flex: `0 0 ${chartXrayPaneFraction * 100}%`,
                      minWidth: 0,
                    }
                  : undefined
              }
            >
              <div
                className={
                  showXraySplit
                    ? 'charting-chart-xray-split-chart-half'
                    : 'charting-chart-full-slot'
                }
              >
                <div
                  className={`charting-chart-section w-100 ${
                    showXraySplit ? 'min-h-0' : 'flex-shrink-0'
                  }`}
                >
                  <div className="charting-chart-content">
                    {toothChartElement}
                  </div>
                  <div className="charting-toggle-rail">
                    <ChartingViewToggle
                      currentView={currentView}
                      onViewChange={handleViewChange}
                    />
                  </div>
                </div>
              </div>
              {showXraySplit ? (
                <div className="charting-xray-master-detail-wrap">
                  <ChartingSidePanel
                    selectedTooth={selectedTooth}
                    activeChartId={activeDentalChartId}
                    onApplyCondition={handleApplyCondition}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    sidePanelLayout="xray-split"
                    voiceStatus={voiceStatus}
                    voiceActive={voiceActive}
                    chartingInfoFocusTick={chartingInfoFocusTick}
                  />
                </div>
              ) : null}
            </div>
            {showXraySplit ? (
              <>
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-valuenow={Math.round(chartXrayPaneFraction * 100)}
                  aria-valuemin={CHART_XRAY_PANE_MIN_PCT}
                  aria-valuemax={CHART_XRAY_PANE_MAX_PCT}
                  tabIndex={0}
                  className="charting-xray-split-handle"
                  onMouseDown={onChartXraySplitHandleMouseDown}
                  style={{
                    flex: `0 0 ${CHART_XRAY_HANDLE_PX}px`,
                  }}
                />
                <div className="charting-xray-split-viewer flex-grow-1 min-w-0 d-flex min-h-0">
                  <EmbeddedXrayViewer
                    ref={embeddedViewerRef}
                    initialFiles={initialEmbeddedFiles}
                    isHostVisible={isHostVisible}
                  />
                </div>
              </>
            ) : null}
          </div>
          <div className="mt-2">
            <ChartingHistory
              onViewNote={onViewNote}
              onSelectHistory={handleSelectHistory}
              gridRef={gridRef}
              layoutSyncKey={showXraySplit ? 'xray-split' : 'full'}
            />
          </div>
        </Col>

        {!isFullscreen && !showXraySplit && (
          <Col md="auto" className="mh-100">
            <ChartingSidePanel
              selectedTooth={selectedTooth}
              activeChartId={activeDentalChartId}
              onApplyCondition={handleApplyCondition}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              sidePanelLayout="accordion"
              voiceStatus={voiceStatus}
              voiceActive={voiceActive}
              chartingInfoFocusTick={chartingInfoFocusTick}
            />
          </Col>
        )}
      </Row>
    </>
  )
}

export default ChartingMain
